<template>
  <div class="tenders-data-table" :class="{ 'is-overflowing': tableOverflows }">
    <BasePullToRefresh v-if="isTouchScreen" :pending="pending" @refresh="refreshTenders" />
    <div class="tenders-data-table__results-section">
      <span class="tenders-data-table__count">
        <span class="number-monospace">{{ formatLocalizedNumber(totalCount) }}</span>
        {{ ' ' + $t('page.tenders.results') }}
      </span>
      <div class="tenders-data-table__buttons">
        <BasePhoneListControls
          v-if="isPhone && hasResults"
          :selecting.sync="phoneSelecting"
          @sort="phoneSortOpen = true"
        />
        <BaseRoundButton
          :size="36"
          :tooltip-text="exportTooltip"
          class="tender-header__actions-item"
          icon="download"
          :isLoading="exportPending"
          :disabled="hasReachExportLimit || !hasResults || tenantIsStandard()"
          is-white
          @click.native="exportTenders"
        />

        <!-- On a touch phone, pulling the list down refreshes it too. -->
        <BaseRefreshButton :pending="pending" @refresh="refreshTenders" />
      </div>
    </div>

    <div v-if="hasResults" class="tenders-data-table__content">
      <TendersPhoneList
        v-if="isPhone"
        :tenders="tenders"
        :mode="mode"
        :selecting.sync="phoneSelecting"
        :sort-open.sync="phoneSortOpen"
        :show-score="showScoreColumn"
        :archived-by="getArchivedBy"
        :has-more="nextPage !== null"
        :loading-more="loadingMore"
        :load-more-error="loadMoreError"
        :pending="pending"
        :sorting-field="sortingField"
        :sorting-order="sortingOrder"
        :assignable-users="assignableUsers"
        :show-opportunities="tenantHasOpportunitiesAccess()"
        @open="(id) => $emit('rowClicked', id)"
        @load-more="$emit('load-more')"
        @follow="(tender) => onFollowClick(tender, tender.userTender.followed)"
        @share="(tender) => onShareClick(tender, tender.tenantTender.shared)"
        @rate-up="onRateUp"
        @rate-down="onRateDown"
        @read="onReadToggle"
        @archive="onArchiveToggle"
        @sort="onPhoneSort"
        @assign="onAssign"
        @convert="convertToOpportunity"
        @open-opportunity="(tender, opportunity) => handleOpportunityClick(opportunity)"
      />
      <TableNormal
        v-else
        v-model="selectedTenders"
        :fixed-header="true"
        :headers="filteredHeaders"
        :items="tenders"
        :items-per-page="itemsPerPage"
        :page.sync="currentPage"
        :single-select="false"
        @click:row="onRowClick"
        hide-default-footer
        item-key="id"
        show-select
        :must-sort="false"
        @update:sort-by="setSortingField"
        @update:sort-desc="setSortingOrder"
        :mobile-breakpoint="1280"
      >
        <template v-slot:header.data-table-select="{}">
          <FormCheckbox
            :indeterminate="partiallySelected"
            :afterIndeterminate="false"
            :onChange="onSelectAllClick"
            :value="allAreSelected"
            class="tenders-data-table__check-all"
          />
          <TendersActionsManager
            v-if="hasSelection"
            :mode="mode"
            :tenders="selectedTenders"
            @tender-status-updated="unselectAll"
          />
        </template>
        <template v-slot:header.fromPlatform="{}">
          <BaseIcon name="layers" class="tenders-data-table__platform-icon" />
        </template>
        <template v-slot:header.score="{ header }">
          <span class="tenders-data-table__score-header">
            {{ header.text }}
            <BaseIcon
              v-tooltip="{ content: $t('search-filters.relevance-score.tooltip'), placement: 'top' }"
              name="info"
              class="tenders-data-table__score-info"
              role="img"
              tabindex="0"
              :aria-label="$t('search-filters.relevance-score.tooltip')"
            />
          </span>
        </template>
        <template v-slot:item.data-table-select="{ isSelected, select }">
          <FormCheckbox :value="isSelected" @input="select($event)" @click.native.stop />
        </template>
        <template v-slot:item.fromPlatform="{ item }">
          <div class="tenders-data-table__platform">
            <v-popover placement="top" trigger="hover" popoverClass="tender-bottom-nav__nav-tooltip">
              <PlatformIcon :platform="item.source.id" />
              <template v-slot:popover>
                <div class="tenders-data-table__favicon-tooltip">
                  <div>{{ platformName(item.source.id) }}</div>
                  <div v-if="getSourceTags(item.source.id).length" class="tenders-data-table__platform-tags">
                    <BaseTag
                      v-for="(tag, tagIndex) in getSourceTags(item.source.id)"
                      :key="`${tag}-${tagIndex}`"
                      :tone="getPlatformTagTone(tag)"
                      preset="soft"
                      :icon="getPlatformTagIcon(tag)"
                    >
                      {{ $t('tags.' + tag) }}
                    </BaseTag>
                  </div>
                </div>
              </template>
            </v-popover>
            <span class="tenders-data-table__platform-name"> {{ platformName(item.source.id) }}</span>
          </div>
        </template>
        <template v-slot:item.score="{ item }">
          <div v-if="item.score !== null">
            <DynamicThermometer :value="item.score" />
          </div>
          <div
            v-else
            class="tenders-data-table__empty-score"
            v-tooltip="{ content: $t('search-filters.relevance-score.missing') }"
          >
            N/A
          </div>
        </template>
        <template v-slot:item.title="{ item }">
          <div class="tenders-data-table__row-title-container">
            <span
              :id="'item-' + item.id"
              :class="{
                'is-read': item.userTender.read,
                'is-unread': !item.userTender.read,
              }"
              class="tenders-data-table__row-title"
            >
              <TextHighlight :content="item.title"></TextHighlight>
            </span>
            <div class="tenders-data-table__row-subtitle">
              <BaseTag v-if="item.source && item.source.referenceNumber" tone="neutral" preset="soft">
                #{{ item.source.referenceNumber }}
              </BaseTag>
              <!-- #{{ item.source.referenceNumber }} -->

              <BaseTag v-if="item.documents.length > 0" tone="neutral" preset="soft" icon="file-text">
                {{ item.documents.length }}
              </BaseTag>
              <!-- <BaseIcon class="base-informational-document__icon" name="file-text" />
                {{ item.documents.length }} -->

              <template v-if="item.matchedKeywords">
                <v-popover v-if="item.matchedKeywords.length > 0" trigger="hover" placement="top">
                  <BaseTag tone="info" preset="secondary" icon="type">
                    <template v-if="isExploreMode">
                      {{
                        $tc('page.tenders.covered-by-keywords', item.matchedKeywords.length, {
                          count: item.matchedKeywords.length,
                        })
                      }}
                    </template>
                    <template v-else>
                      {{ item.matchedKeywords.length }}
                    </template>
                  </BaseTag>
                  <template v-slot:popover>
                    <div class="tenders-data-table__matched-keywords">
                      <Keyword v-for="(keyword, index) in item.matchedKeywords" :key="index" isResult>
                        {{ keyword }}
                      </Keyword>
                    </div>
                  </template>
                </v-popover>
                <BaseTag v-else tone="warning" preset="secondary" icon="type">
                  {{ $t('page.tenders.not-covered-by-keywords') }}
                </BaseTag>
              </template>
              <BaseTag v-else-if="matchesPending" skeleton />
            </div>

            <span v-if="item.tenantTender && item.tenantTender.archived" class="tenders-data-table__subtitle">{{
              getArchivedBy(item)
            }}</span>
          </div>
        </template>
        <template v-slot:item.matchedKeywords="{ item }">
          <template v-if="item.matchedKeywords">
            <v-popover trigger="hover" placement="top">
              <div v-if="item.matchedKeywords.length > 0" class="tenders-data-table__matched-keywords-icon">
                {{ item.matchedKeywords.length }}
              </div>
              <template v-slot:popover>
                <div class="tenders-data-table__matched-keywords">
                  <Keyword v-for="(keyword, index) in item.matchedKeywords" :key="index" isResult>
                    {{ keyword }}
                  </Keyword>
                </div>
              </template>
            </v-popover>
          </template>
          <div v-else-if="!matchesPending" class="tenders-data-table__no-keywords">{{ $t('page.tenders.none') }}</div>
        </template>
        <template v-slot:item.emitter="{ item }">
          <span
            v-if="item.emitter"
            :class="{
              'is-read': item.userTender.read,
              'is-unread': !item.userTender.read,
            }"
          >
            {{ item.emitter.name }}
          </span>
        </template>

        <template v-slot:item.opportunities="{ item }">
          <template v-if="item.opportunities.length > 0">
            <BaseOpportunityTag
              v-for="(opportunity, index) in item.opportunities"
              :key="index"
              :opportunity="opportunity"
              @click.native.stop
              @onOpportunityClick="handleOpportunityClick(opportunity)"
              :tooltip-text="$t('actions.consult')"
            />
          </template>
        </template>
        <template v-slot:item.status="{ item }">
          <div class="tenders-data-table__status">
            <TenderStatusTag :status="item.status" />
          </div>
        </template>
        <template v-slot:item.publishedSortable="{ item }">
          <span
            :class="{
              'is-read': item.userTender.read,
              'is-unread': !item.userTender.read,
            }"
            :title="`${$t('actions.added')}: ${formatDate(item.added || item.created, 'llll')}`"
          >
            {{ formatShortDate(toMoment(item.published)) }}
          </span>
        </template>
        <template v-slot:item.closingSortable="{ item }">
          <div class="tenders-data-table__closing-date">
            <div class="tenders-data-table__closing-date-text">
              <span :class="[{ 'is-read-date': item.userTender.read, 'is-unread-date': !item.userTender.read }]">
                {{ formatShortDate(toMoment(item.closingDate)) }}
              </span>
            </div>
            <BaseTag
              v-if="item.daysBeforeClosing && item.daysBeforeClosing !== 0 && isClosingCountdownVisible(item.status)"
              icon="clock"
              preset="soft"
              :tone="getClosingCountdownTone(item.daysBeforeClosing)"
            >
              {{ getDhm(item.daysBeforeClosing).split(' ')[0] }}
            </BaseTag>
          </div>
        </template>
        <template v-slot:item.assignation="{ item }">
          <!-- Floating, not attached inside the row: when the table scrolls
               sideways its box clips anything below it, so an attached menu on
               the last rows would be cut off. It escapes like the "⋯" menu. -->
          <div @click.stop="">
            <BaseAssignation
              :ref="`assignation-${item.id}`"
              :value="item.assignees"
              floating
              @change="onAssign(item, ...arguments)"
            />
          </div>
        </template>
        <template v-slot:item.actions="{ item }">
          <!-- Narrow table: one "⋯" menu instead of four buttons, which take a whole column. -->
          <v-popover
            v-if="compactActions"
            placement="bottom-end"
            trigger="click"
            popoverClass="tenders-data-table__actions-popover"
            class="tenders-data-table__actions-toggle"
            @click.native.stop
          >
            <BaseRoundButton
              :size="35"
              icon="more-horizontal"
              is-white
              is-neutral
              :aria-label="$t('actions.more-actions')"
              :tooltip-text="$t('actions.more-actions')"
            />
            <template v-slot:popover>
              <ul class="tenders-data-table__actions-menu">
                <li :class="{ 'is-active': item.userTender.followed }">
                  <button v-close-popover @click="onFollowClick(item, item.userTender.followed)">
                    <BaseIcon name="bookmark" :size="18" />
                    {{ item.userTender.followed ? $t('actions.unfollow') : $t('actions.follow') }}
                  </button>
                </li>
                <li :class="{ 'is-active': item.tenantTender.shared }">
                  <button v-close-popover @click="onShareClick(item, item.tenantTender.shared)">
                    <BaseIcon name="share-2" :size="18" />
                    {{ item.tenantTender.shared ? $t('actions.unshare') : $t('actions.share') }}
                  </button>
                </li>
                <li :class="{ 'is-active is-down': item.tenantTender.thumbsRating === 'DOWN' }">
                  <button v-close-popover @click="onRateDown(item)">
                    <BaseIcon name="thumbs-down" :size="18" />
                    {{
                      item.tenantTender.thumbsRating === 'DOWN'
                        ? $t('actions.undislike')
                        : $tc('tender-details.thumbs-rating.down-message.title', 1)
                    }}
                  </button>
                </li>
                <li :class="{ 'is-active is-up': item.tenantTender.thumbsRating === 'UP' }">
                  <button v-close-popover @click="onRateUp(item)">
                    <BaseIcon name="thumbs-up" :size="18" />
                    {{
                      item.tenantTender.thumbsRating === 'UP'
                        ? $t('actions.unlike')
                        : $tc('tender-details.thumbs-rating.up-message.title', 1)
                    }}
                  </button>
                </li>
              </ul>
            </template>
          </v-popover>
          <div v-else class="tenders-data-table__actions">
            <BaseRoundButton
              :is-light="item.userTender.followed"
              :is-neutral="!item.userTender.followed"
              :size="35"
              :tooltip-text="item.userTender.followed ? $t('actions.unfollow') : $t('actions.follow')"
              :tooltip-subtitle="$t('page.tenders.follow-tooltip-subtitle')"
              class="tender-header__actions-item"
              icon="bookmark"
              is-white
              @click.native.stop="onFollowClick(item, item.userTender.followed)"
            />
            <BaseRoundButton
              :is-light="item.tenantTender.shared"
              :is-neutral="!item.tenantTender.shared"
              :size="35"
              :tooltip-text="item.tenantTender.shared ? $t('actions.unshare') : $t('actions.share')"
              :tooltip-subtitle="$t('page.tenders.share-tooltip-subtitle')"
              class="tender-header__actions-item"
              icon="share-2"
              is-white
              @click.native.stop="onShareClick(item, item.tenantTender.shared)"
            />
            <BaseRoundButton
              :size="35"
              icon="thumbs-down"
              is-white
              :is-neutral="item.tenantTender.thumbsRating === 'UP' || item.tenantTender.thumbsRating === null"
              :is-red="item.tenantTender.thumbsRating === 'DOWN'"
              :tooltip-text="
                item.tenantTender.thumbsRating === 'DOWN'
                  ? $t('actions.undislike')
                  : $tc('tender-details.thumbs-rating.down-message.title', 1)
              "
              :tooltip-subtitle="$t('tender-details.thumbs-rating.down-message.subtitle')"
              @click.native.stop="onRateDown(item)"
            />
            <BaseRoundButton
              :size="35"
              icon="thumbs-up"
              is-white
              :is-neutral="item.tenantTender.thumbsRating === 'DOWN' || item.tenantTender.thumbsRating === null"
              :is-green="item.tenantTender.thumbsRating === 'UP'"
              :tooltip-text="
                item.tenantTender.thumbsRating === 'UP'
                  ? $t('actions.unlike')
                  : $tc('tender-details.thumbs-rating.up-message.title', 1)
              "
              :tooltip-subtitle="$t('tender-details.thumbs-rating.up-message.subtitle')"
              @click.native.stop="onRateUp(item)"
            />
          </div>
        </template>
      </TableNormal>

      <div v-if="totalPages > 1 && !isPhone" class="tenders-data-table__pagination">
        <TablePagination
          v-model="currentPage"
          :disabled="pending"
          :length="totalPages"
          :total-visible="isMobile ? 6 : 10"
          @input="onInputPage"
        />
      </div>
    </div>

    <TableState
      v-if="!hasResults"
      :state-loading="!hasResults && pending"
      :state-no-data="!hasResults && !pending"
      :state-text="pending ? $t('page.tenders.loading') : $t('page.tenders.no-tender')"
    />
  </div>
</template>

<script>
import BaseAssignation from '@/components/base/BaseAssignation';
import BaseOpportunityTag from '@/components/base/BaseOpportunityTag';
import BasePhoneListControls from '@/components/base/BasePhoneListControls.vue';
import BasePullToRefresh from '@/components/base/BasePullToRefresh.vue';
import BaseIcon from '@/components/base/BaseIcon';
import BaseRefreshButton from '@/components/base/BaseRefreshButton.vue';
import BaseRoundButton from '@/components/base/BaseRoundButton.vue';
import DynamicThermometer from '@/components/util/DynamicThermometer';
import FormCheckbox from '@/components/form/FormCheckbox.vue';
import PlatformIcon from '@/components/PlatformIcon';
import TableNormal from '@/components/table/TableNormal.vue';
import TablePagination from '@/components/table/TablePagination.vue';
import TableState from '@/components/table/TableState.vue';
import TenderStatusTag from '@/components/page-tender-details/TenderStatusTag.vue';
import TendersActionsManager from '@/components/page-tenders/TendersActionsManager.vue';
import TendersPhoneList from '@/components/page-tenders/TendersPhoneList.vue';
import DebounceMixin from '@/mixins/debounce-mixin';
import IsMobileMixin from '@/mixins/is-mobile-mixin';
import Keyword from '@/components/page-keywords/Keyword';
import PlatformName from '@/mixins/platform-name-mixin';
import BaseTag from '@/components/base/BaseTag.vue';
import TextHighlight from '@/components/text-highlight/TextHighlight';
import { getPlatformTagTone, getPlatformTagIcon } from '@/util/tag-tones/platform-tags';
import { getClosingCountdownTone, isClosingCountdownVisible } from '@/util/tender-closing-countdown';
import { getDhm, formatShortDate, formatDate, toMoment } from '@/util/date-util';
import { getSortingField, getSortingOrder } from '@/util/sorting-util';
import { Modes } from '@/models/modes';
import { tenantIsStandard } from '@/util/subscription-util';
import { tenantHasOpportunitiesAccess, tenantHasTenderScoringAccess } from '@/util/module-access-util';
import { bus } from '@/main';
import { removeMarks } from '@/util/mark-util';
import { formatLocalizedNumber } from '@/util/number-util';
import { assignableUsers } from '@/util/user-util';
import { viewport } from '@/util/viewport';
import { mapActions, mapGetters } from 'vuex';

const COMPACT_ACTIONS_BELOW_PX = 1420;

export default {
  name: 'TendersDataTable',
  mixins: [IsMobileMixin, PlatformName, DebounceMixin],
  components: {
    BaseAssignation,
    BaseOpportunityTag,
    BasePhoneListControls,
    BasePullToRefresh,
    BaseIcon,
    BaseRefreshButton,
    BaseRoundButton,
    DynamicThermometer,
    FormCheckbox,
    PlatformIcon,
    BaseTag,
    TableNormal,
    TableState,
    TablePagination,
    TenderStatusTag,
    TendersActionsManager,
    TendersPhoneList,
    TextHighlight,
    Keyword,
  },
  props: {
    mode: String,
    tenders: Array,
  },
  data() {
    return {
      selectedTenders: [],
      itemsPerPage: 25,
      currentPage: null,
      exportMax: 500,
      sortingRefreshTimeout: null,
      // Set from the table's measured width; see observeTableWidth().
      compactActions: false,
      tableOverflows: false,
      resizeObserver: null,
      phoneSelecting: false,
      phoneSortOpen: false,
      // Inbox and Explore both keep their table alive; only the one on screen reloads the list.
      isActive: true,
    };
  },
  computed: {
    ...mapGetters('tenders', [
      'page',
      'totalPages',
      'totalCount',
      'matchesPending',
      'pending',
      'nextPage',
      'loadingMore',
      'loadMoreError',
      'sortingField',
      'sortingOrder',
    ]),
    isPhone() {
      return viewport.size === 'phone';
    },
    isTouchScreen() {
      return !viewport.canHover;
    },
    showScoreColumn() {
      return this.tenantHasTenderScoringAccess() && !this.isExploreMode && this.hasKeywordFilter;
    },
    ...mapGetters('exportTenders', { exportPending: 'pending' }),
    ...mapGetters('tenantModules', ['tenantModules']),
    ...mapGetters('userPlatforms', ['userPlatforms']),
    ...mapGetters('tenant', ['users', 'roleUserOnly']),
    ...mapGetters('auth', ['currentUser']),
    assignableUsers() {
      return assignableUsers(this.currentUser, this.users, this.roleUserOnly);
    },
    ...mapGetters('searchFilters', ['searchFilters']),
    // The relevance score is only meaningful when the view filters on keywords,
    // so the column follows the active view (in addition to the tenant access gate).
    // Matches the score chip's gate: specific keywordIds OR the "all keywords" flag.
    hasKeywordFilter() {
      const f = this.searchFilters || {};
      return (f.keywordIds || []).length > 0 || !!f.allKeywords;
    },
    hasReachExportLimit() {
      return this.totalCount > this.exportMax;
    },
    exportTooltip() {
      if (this.tenantIsStandard()) return this.$t('subscriptions.message');
      return this.hasReachExportLimit || !this.hasResults
        ? this.$t('page.tenders.export-max', { max: this.exportMax })
        : this.$tc('page.tenders.export', this.formatLocalizedNumber(this.totalCount), {
            count: this.formatLocalizedNumber(this.totalCount),
          });
    },
    hasSelection() {
      return this.selectedTenders.length > 0;
    },
    hasResults() {
      return this.tenders.length > 0;
    },
    allAreSelected() {
      return this.hasResults && this.tenders.length === this.selectedTenders.length;
    },
    partiallySelected() {
      return this.hasResults && this.hasSelection && this.tenders.length !== this.selectedTenders.length;
    },
    filteredHeaders() {
      return this.headers.filter((header) => header.show !== false);
    },
    isExploreMode() {
      return this.mode === Modes.EXPLORE.name;
    },
    headers() {
      return [
        { text: '', value: 'fromPlatform', sortable: true, width: '56px' },
        {
          text: this.$t('page.tenders.data-table-titles.title'),
          value: 'title',
          sortable: true,
          width: '35%',
        },
        {
          text: this.$t('search-filters.relevance-score.name'),
          value: 'score',
          sortable: false,
          width: '64px',
          show: this.showScoreColumn,
        },
        {
          text: this.$t('menu.keywords'),
          value: 'matchedKeywords',
          sortable: false,
          width: '90px',
          show: false,
        },
        {
          text: this.$t('page.tenders.data-table-titles.emitter'),
          value: 'emitter',
          sortable: true,
          class: 'tenders-data-table__fixed-size-emitter',
        },

        {
          text: this.$t('page.opportunity.opportunity'),
          value: 'opportunities',
          sortable: false,
          show: this.tenantHasOpportunitiesAccess(),
        },
        { text: this.$t('search-filters.status.name'), value: 'status' },
        {
          text: this.$t('page.tenders.data-table-titles.published'),
          value: 'publishedSortable',
          sortable: true,
          width: '115px',
        },
        {
          text: this.$t('page.tenders.data-table-titles.closing'),
          value: 'closingSortable',
          sortable: true,
          width: '115px',
        },
        {
          text: this.$t('page.tenders.data-table-titles.assignees'),
          value: 'assignation',
          sortable: false,
          // Three avatars and a "+N", or the "Assign" button.
          width: '120px',
        },
        { text: this.$t('page.tenders.data-table-titles.actions'), value: 'actions', sortable: false },
      ];
    },
  },
  methods: {
    ...mapActions('tender', [
      'assign',
      'follow',
      'unfollow',
      'share',
      'unshare',
      'rateUp',
      'rateDown',
      'unRate',
      'read',
      'unread',
      'archive',
      'unarchive',
    ]),
    ...mapActions('tenders', ['setCustomSortingField', 'setCustomSortingOrder', 'setContinuous']),
    ...mapActions('opportunities', ['addOpportunity']),
    tenantIsStandard,
    tenantHasOpportunitiesAccess,
    tenantHasTenderScoringAccess,
    formatShortDate,
    formatDate,
    formatLocalizedNumber,
    toMoment,
    getDhm,
    getPlatformTagTone,
    getPlatformTagIcon,
    getClosingCountdownTone,
    isClosingCountdownVisible,
    getSourceTags(platformId) {
      if (this.userPlatforms.length === 0) return [];
      const platform = this.userPlatforms.find((platform) => platform.id === platformId);
      if (!platform || !platform.tags) return [];
      return platform.tags;
    },
    exportTenders() {
      this.$emit('export-tenders');
    },
    platformName(id) {
      return this.getPlatformName(id);
    },
    // A failed save is reported, and the row's avatars fall back to what is stored.
    onAssign(tender, userIds) {
      return this.assign({ tenderId: tender.id, userIds }).catch((error) => {
        this.$toast.error(error);
        const ref = this.$refs[`assignation-${tender.id}`];
        const assignation = Array.isArray(ref) ? ref[0] : ref;
        if (assignation) assignation.reset();
      });
    },
    onFollowClick(tender, followed) {
      const id = tender.id;

      if (followed) {
        this.$track.tenderUnfollow('tenders_table');
        this.unfollow({ id })
          .then(() =>
            this.$toast.cancel(this.$tc('toast.unfollow'), () => {
              this.follow({ id }).then(() => this.$emit('refresh-tenders', this.page));
            }),
          )
          .catch((error) => this.$toast.error(error));
      } else {
        this.$track.tenderFollow('tenders_table');
        this.follow({ id })
          .then(() => this.$toast.cancel(this.$tc('toast.follow'), () => this.unfollow({ id })))
          .catch((error) => this.$toast.error(error));
      }
    },
    onShareClick(tender, shared) {
      const id = tender.id;

      if (shared) {
        this.$track.tenderUnshare('tenders_table');
        this.unshare({ id })
          .then(() =>
            this.$toast.cancel(this.$tc('toast.unshare'), () => {
              this.share({ id }).then(() => this.$emit('refresh-tenders', this.page));
            }),
          )
          .catch((error) => this.$toast.error(error));
      } else {
        this.$track.tenderShare('tenders_table');
        this.share({ id })
          .then(() => this.$toast.cancel(this.$tc('toast.share'), () => this.unshare({ id })))
          .catch((error) => this.$toast.error(error));
      }
    },
    onRateUp(tender) {
      this.debounce(() => {
        if (tender.tenantTender.thumbsRating === 'UP') {
          this.unRate({ id: tender.id }).catch((error) => this.$toast.error(error));
        } else {
          this.rateUp({ id: tender.id }).catch((error) => this.$toast.error(error));
        }
      }, 250);
    },
    onRateDown(tender) {
      this.debounce(() => {
        if (tender.tenantTender.thumbsRating === 'DOWN') {
          this.unRate({ id: tender.id }).catch((error) => this.$toast.error(error));
        } else {
          this.rateDown({ id: tender.id }).catch((error) => this.$toast.error(error));
        }
      }, 250);
    },
    onRowClick(tender) {
      this.$emit('rowClicked', tender.id);
    },
    handleOpportunityClick(opportunity) {
      this.$emit('open-opportunity', opportunity);
    },
    // From the phone list's sheet, as on the tender's page, with the same undo.
    onReadToggle(tender) {
      const id = tender.id;
      if (tender.userTender.read) {
        this.$track.tenderUnread();
        this.unread({ id })
          .then(() => this.$toast.cancel(this.$tc('toast.unread'), () => this.read({ id })))
          .catch((error) => this.$toast.error(error));
      } else {
        this.$track.tenderRead();
        this.read({ id })
          .then(() => this.$toast.cancel(this.$tc('toast.read'), () => this.unread({ id })))
          .catch((error) => this.$toast.error(error));
      }
    },
    // Archiving takes the tender out of the inbox (and unarchiving out of the archived view):
    // the list reloads, keeping the reader's place.
    onArchiveToggle(tender) {
      const id = tender.id;
      const reload = () => bus.$emit('refresh-page-requested');
      this.$track.tenderArchive();
      if (tender.tenantTender && tender.tenantTender.archived) {
        this.unarchive({ id })
          .then(() => {
            this.$toast.cancel(this.$tc('toast.unarchive'), () => this.archive({ id }).then(reload));
            reload();
          })
          .catch((error) => this.$toast.error(error));
      } else {
        this.archive({ id })
          .then(() => {
            this.$toast.cancel(this.$tc('toast.archive'), () => this.unarchive({ id }).then(reload));
            reload();
          })
          .catch((error) => this.$toast.error(error));
      }
    },
    // As on the tender's page: an opportunity named after the tender.
    convertToOpportunity(tender) {
      this.addOpportunity({ name: removeMarks(tender.title), tenderId: tender.id })
        .then(() => this.$toast.success(this.$tc('page.opportunity-details.opportunity-manipulations.add-success', 1)))
        .catch((error) => this.$toast.error(error));
    },
    onSelectAllClick() {
      if (this.partiallySelected || this.allAreSelected) this.unselectAll();
      else this.selectAll();
    },
    selectAll() {
      this.selectedTenders = [...this.tenders];
    },
    unselectAll() {
      this.selectedTenders = [];
    },
    setSortingOrder(order) {
      const sortingOrder = order.length ? getSortingOrder(order[0]) : 'desc';

      this.setCustomSortingOrder({ sortingOrder });
      this.scheduleSortingRefresh();
    },
    setSortingField(field) {
      const sortingField = field.length ? getSortingField(field[0]) : 'publishedDate';

      this.setCustomSortingField({ sortingField });
      this.scheduleSortingRefresh();
    },
    onPhoneSort({ field, desc }) {
      this.setCustomSortingField({ sortingField: getSortingField(field) });
      this.setCustomSortingOrder({ sortingOrder: getSortingOrder(desc) });
      this.scheduleSortingRefresh();
    },
    // Vuetify emits update:sort-by and update:sort-desc together for one header click.
    // Wait until both Vuex values are updated, then issue a single search request.
    scheduleSortingRefresh() {
      clearTimeout(this.sortingRefreshTimeout);
      this.sortingRefreshTimeout = setTimeout(() => {
        this.sortingRefreshTimeout = null;
        this.refreshTenders();
      }, 0);
    },
    onInputPage(pageNumber) {
      this.$track.tendersTablePageChange();
      this.$emit('pageChanged', pageNumber);
    },
    refreshTenders() {
      this.$emit('refresh-tenders');
    },
    // Below this width the four action buttons are folded into one menu. The
    // full row of buttons takes ~176px; with it and the 120px assignees
    // column, the columns need ~1400px.
    observeTableWidth() {
      if (typeof ResizeObserver === 'undefined') return;

      this.resizeObserver = new ResizeObserver(() => this.measureTable());
      this.resizeObserver.observe(this.$el);
      const table = this.$el.querySelector('table');
      if (table) this.resizeObserver.observe(table);
    },
    measureTable() {
      const compactActions = this.$el.clientWidth < COMPACT_ACTIONS_BELOW_PX;
      const layoutChanges = compactActions !== this.compactActions;
      this.compactActions = compactActions;
      // The other layout only exists after the next render. Measured now, the
      // overflow would be the old columns' and nothing would measure again, so
      // the table could stay stuck scrolling sideways although it now fits.
      if (layoutChanges) this.$nextTick(() => this.measureOverflow());
      else this.measureOverflow();
    },
    // Last resort: if the columns still do not fit, scroll the table sideways
    // inside its own box instead of pushing the whole page. Only then, because
    // a scrolling wrapper stops the pinned header from sticking.
    measureOverflow() {
      if (!this.$el) return;
      const wrapper = this.$el.querySelector('.v-data-table__wrapper');
      const table = wrapper && wrapper.querySelector('table');
      this.tableOverflows = !!table && table.scrollWidth > wrapper.clientWidth + 1;
    },
    checkStickyHeader() {
      if (!this.hasResults) return;

      const stickyDiv = document.querySelector('thead');
      if (stickyDiv) {
        const stickyDivOffset = stickyDiv.offsetTop;

        if (stickyDivOffset > 0) {
          stickyDiv.classList.add('is-pinned');
        } else {
          stickyDiv.classList.remove('is-pinned');
        }
      }
    },
    getArchivedBy(tender) {
      if (tender.tenantTender && tender.tenantTender.archived) {
        const user = this.users.find((user) => user.id === tender.tenantTender.archivedBy);
        if (user) {
          return this.$t('tender-details.archived-by', {
            name: user.fullName,
            date: formatShortDate(toMoment(tender.tenantTender.archivedDate)),
          });
        }
        return this.$t(
          tender.tenantTender.archivedBy ? 'tender-details.archived-by-former-user' : 'tender-details.archived-on',
          { date: formatShortDate(toMoment(tender.tenantTender.archivedDate)) },
        );
      }
      return '';
    },
  },
  mounted() {
    this.currentPage = this.page;
    this.observeTableWidth();

    const content = document.querySelector('.base-layout__content');
    content.addEventListener('scroll', (e) => {
      this.checkStickyHeader();
    });
  },
  watch: {
    tenders() {
      this.selectedTenders = [];
    },
    page() {
      this.currentPage = this.page;
    },
    isPhone: {
      immediate: true,
      handler(isPhone, wasPhone) {
        this.setContinuous(isPhone);
        if (wasPhone === undefined || !this.isActive) return;
        // Crossing the phone width (a rotation, a resized window): the phone list grows from
        // page 1 and the table shows one page, so reload what the new layout expects.
        const pagesListed = this.tenders.length > this.itemsPerPage;
        if ((isPhone && this.page > 1) || (!isPhone && pagesListed)) this.refreshTenders();
        this.phoneSelecting = false;
      },
    },
  },
  activated() {
    this.isActive = true;
  },
  deactivated() {
    this.isActive = false;
  },
  beforeDestroy() {
    clearTimeout(this.sortingRefreshTimeout);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    window.removeEventListener('scroll', this.checkStickyHeader);
  },
};
</script>

<style lang="scss">
.tenders-data-table {
  display: flex;
  flex: auto;
  flex-direction: column;
  padding: 0px;

  // HAVE NOT DECIDED YET, SHADOW DONT WORK
  // tr:has(.is-read) {
  //   background: $color-neutral-pale !important;
  // }

  .v-data-table--fixed-header {
    padding: 0 !important;
  }

  .v-data-table.theme--light {
    border-radius: $border-radius-root;

    table {
      border-collapse: separate;
      position: relative;
      background: $color-white;
      border-radius: $border-radius-root;
    }

    thead th {
      background-color: $color-white;
      border-bottom: 0.5px solid $color-border !important;

      &:first-child {
        border-radius: $border-radius-root 0 0 0;
      }

      &:last-child {
        border-radius: 0 $border-radius-root 0 0;
      }
    }

    .v-data-table-header {
      position: sticky !important;
      top: 126px !important;
      z-index: 10;
      border-radius: $border-radius-root;
      transition: box-shadow 0.2s;

      &.is-pinned {
        box-shadow: $shadow-tree-all;
        -webkit-box-shadow: $shadow-tree-all;
        transition: box-shadow 0.2s;
      }
    }
  }

  &__results-section {
    display: flex;
    align-items: center;
    // The same room before the list as on the other lists.
    padding-bottom: $space-3;
    justify-content: space-between;

    // The phone's sort and select buttons: the count keeps one line, the buttons go under it.
    @include screen-below('tablet') {
      flex-wrap: wrap;
      row-gap: 4px;

      .tenders-data-table__count {
        white-space: nowrap;
      }

      .tenders-data-table__buttons {
        margin-left: auto;
      }
    }
  }

  &__buttons {
    display: flex;
    gap: 8px;
  }

  &__count {
    color: $color-neutral;
    font-size: $font-size-small;
    padding: 4px 12px;
    text-transform: uppercase;
  }

  &__content {
    flex: auto;
    margin-bottom: 96px;
    position: relative;
  }

  &__row-title {
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    &-container {
      display: flex;
      flex-direction: column;
    }
  }

  &__row-subtitle {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-top: 4px;
    color: $color-dark-blue-pale;
  }

  &__favicon-tooltip {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
  }

  &__platform-tags {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px;
  }

  &__subtitle {
    font-size: $font-size-xsmall;
    font-weight: 600;
    color: $color-dark-blue-pale;
    padding-top: 4px;
  }

  &__matched-keywords {
    max-width: min-content;
    flex-wrap: wrap;
    padding: 8px;
    gap: 4px;
    display: flex;
    flex-direction: column;
    justify-items: center;
  }

  &__matched-keywords-icon {
    background: rgba($color-accent, 0.1);
    border-radius: $border-radius-round;
    max-width: min-content;
    padding: 2px 6px;
  }

  &__no-keywords {
    color: $color-neutral;
  }

  &__matched-keywords-header {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__matched-keywords-see-more {
    text-decoration: underline;
    font-size: 12px;
    display: block;
    width: 100%;
    margin-top: 4px;
    margin-left: 4px;

    span {
      border-radius: $border-radius-medium;
      padding: 4px;

      &:hover {
        color: $color-normal;
        background: $color-neutral-pale;
      }
    }
  }

  &__check-all {
    z-index: $third-index;
    width: min-content;
  }

  &__closing-date {
    display: flex;
    flex-direction: column;
    gap: 4px;
    align-items: flex-start;
    justify-content: center;

    .tag {
      margin-left: -4px;
    }
  }

  &__closing-date-text {
    min-width: max-content;
  }

  &__actions {
    display: flex;
  }

  // See measureTable(): only while the columns do not fit. As specific as
  // TableNormal's fixed-header rule that resets the wrapper's overflow.
  &.is-overflowing .v-data-table.v-data-table--fixed-header .v-data-table__wrapper {
    overflow-x: auto;
  }

  // Inside a scrolling wrapper the header's sticky top offset would be counted
  // from the wrapper, pushing the header down over the rows. Unpin it there.
  &.is-overflowing .v-data-table.theme--light .v-data-table-header {
    position: relative !important;
    top: 0 !important;
  }

  &__actions-popover {
    background-color: $color-white;
    border-radius: $border-radius-small;
    box-shadow: $shadow-center;

    .popover-arrow {
      display: none;
    }
  }

  &__actions-menu {
    list-style: none;
    margin: 0;
    padding: 8px 0;

    button {
      display: flex;
      align-items: center;
      gap: 12px;
      width: 100%;
      padding: 8px 16px;
      text-align: left;
      white-space: nowrap;
      transition: color 200ms ease;

      &:hover {
        color: $color-accent;
      }
    }

    .is-active button {
      color: $color-accent;
    }

    .is-down button {
      color: $color-error;
    }

    .is-up button {
      color: $color-success;
    }
  }

  &__platform-icon {
    width: 16px;
    height: 16px;
    margin-top: 4px;
  }

  &__platform {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__platform-name {
    display: none;
  }

  &__fixed-size-emitter {
    width: 25%;
  }

  &__fixed-size-closing-date {
    width: 120px;
  }

  &__status {
    display: flex;
  }

  &__pagination {
    position: fixed;
    bottom: 32px;
    left: 50%;
    transform: translateX(-50%);

    .v-pagination--disabled {
      opacity: 1;
    }

    &--no-sidebar {
      width: calc(100% - 4.25rem);
    }
  }

  &__empty-score {
    font-size: 12px;
    color: $color-neutral;
    align-items: center;
    display: flex;
  }

  // The header's own colour and size, so the icon reads as part of the label.
  &__score-header {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    line-height: 1;
  }

  &__score-info {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    color: inherit;
    cursor: help;

    &:focus-visible {
      outline: 2px solid $color-accent;
      outline-offset: 2px;
      border-radius: 50%;
    }
  }

  @media screen and (max-width: 1279px) {
    &__platform-name {
      display: block;
    }
  }
}
</style>
